import React, { useEffect, useState, useRef, useMemo } from "react";
import axios from "../utils/axiosInstance";
import {
  Modal,
  Button,
  Form,
  Table,
  Row,
  Col,
  Card,
  Badge,
  Spinner,
  Alert,
  InputGroup,
  Pagination,
} from "react-bootstrap";
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap-icons/font/bootstrap-icons.css";

const API_URL = "/isibo";

// Helper: decode JWT payload without verification
const decodeToken = () => {
  try {
    const token = localStorage.getItem("token");
    if (!token) return null;
    return JSON.parse(atob(token.split(".")[1]));
  } catch { return null; }
};

const Isibo = () => {
  // ────────────────────────────────────────────────
  // Core States
  // ────────────────────────────────────────────────
  const [isiboList, setIsiboList] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [show, setShow] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [alert, setAlert] = useState({ show: false, message: "", variant: "" });
  const [searchTerm, setSearchTerm] = useState("");
  const [sortBy, setSortBy] = useState("name");
  const [sortOrder, setSortOrder] = useState("asc");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(8);
  const [stats, setStats] = useState({
    total: 0, totalMembers: 0, avgMembersPerCoop: 0,
    largestCoop: null, uniqueVillages: 0,
  });
  const [assignedVillage, setAssignedVillage] = useState("");
  const [formData, setFormData] = useState({
    izina_ry_isibo: "", umuyobozi_w_isibo: "",
    umubare_w_abaturage: "", umudugudu: "",
  });
  const [formErrors, setFormErrors] = useState({});

  // Determine if the current user is an Isibo user (has role in token)
  const tokenPayload = useMemo(() => decodeToken(), []);
  const isIsiboUser = !!(tokenPayload?.role); // Isibo/Umunyamabanga have role; Mudugudu does not

  // ─── Responsive States ──────────────────────────────────
  const [windowWidth, setWindowWidth] = useState(
    typeof window !== "undefined" ? window.innerWidth : 1200
  );
  const [windowHeight, setWindowHeight] = useState(
    typeof window !== "undefined" ? window.innerHeight : 800
  );
  const [orientation, setOrientation] = useState(
    typeof window !== "undefined" && window.innerWidth > window.innerHeight
      ? "landscape" : "portrait"
  );
  const [useCardView, setUseCardView] = useState(false);
  const [showMobileSort, setShowMobileSort] = useState(false);
  const [keyboardVisible, setKeyboardVisible] = useState(false);

  const topRef = useRef(null);

  // ─── Breakpoints ────────────────────────────────────────
  const isExtraSmall = windowWidth <= 360;
  const isSmall = windowWidth <= 576;
  const isMobile = windowWidth <= 768;
  const isTablet = windowWidth > 768 && windowWidth <= 1024;
  const isDesktop = windowWidth > 1024;
  const isLandscape = orientation === "landscape";
  const isShortScreen = windowHeight <= 500;

  // ─── Theme ──────────────────────────────────────────────
  const theme = {
    bg: "var(--theme-bg, #0a0e1a)",
    cardBg: "var(--theme-bg-card, rgba(15,23,42,0.65))",
    text: "var(--theme-text-main, #f1f5f9)",
    textMuted: "var(--theme-text-muted, #94a3b8)",
    primary: "#6366f1",
    primaryDark: "#4f46e5",
    success: "#10b981",
    warning: "#f59e0b",
    danger: "#ef4444",
    info: "#06b6d4",
    border: "rgba(99,102,241,0.12)",
  };

  // ─── Responsive Detection ──────────────────────────────
  useEffect(() => {
    const handleResize = () => {
      const w = window.innerWidth;
      const h = window.innerHeight;
      setWindowWidth(w);
      setWindowHeight(h);
      setOrientation(w > h ? "landscape" : "portrait");
      if (w <= 576) { setItemsPerPage(5); setUseCardView(true); }
      else if (w <= 768) { setItemsPerPage(6); setUseCardView(true); }
      else { setItemsPerPage(8); setUseCardView(false); }
    };

    const handleOrientationChange = () => setTimeout(handleResize, 150);

    const handleFocusIn = (e) => {
      if (["INPUT", "TEXTAREA", "SELECT"].includes(e.target.tagName)) {
        if (window.innerWidth <= 768) setKeyboardVisible(true);
      }
    };
    const handleFocusOut = () => setKeyboardVisible(false);

    const handleViewportResize = () => {
      if (window.visualViewport) {
        setKeyboardVisible(
          window.visualViewport.height < window.innerHeight * 0.75
        );
      }
    };

    window.addEventListener("resize", handleResize);
    window.addEventListener("orientationchange", handleOrientationChange);
    document.addEventListener("focusin", handleFocusIn);
    document.addEventListener("focusout", handleFocusOut);
    if (window.visualViewport)
      window.visualViewport.addEventListener("resize", handleViewportResize);

    handleResize();

    return () => {
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("orientationchange", handleOrientationChange);
      document.removeEventListener("focusin", handleFocusIn);
      document.removeEventListener("focusout", handleFocusOut);
      if (window.visualViewport)
        window.visualViewport.removeEventListener("resize", handleViewportResize);
    };
  }, []);

  // ─── Viewport Setup ────────────────────────────────────
  useEffect(() => {
    let vp = document.querySelector('meta[name="viewport"]');
    if (!vp) { vp = document.createElement("meta"); vp.name = "viewport"; document.head.appendChild(vp); }
    vp.content = "width=device-width, initial-scale=1, maximum-scale=1, user-scalable=no, viewport-fit=cover";

    
    return () => { document.body.style.overscrollBehavior = ""; };
  }, []);

  // ────────────────────────────────────────────────
  // Helpers
  // ────────────────────────────────────────────────
  const calculateStats = (data) => {
    if (!data?.length) {
      setStats({ total: 0, totalMembers: 0, avgMembersPerCoop: 0, largestCoop: null, uniqueVillages: 0 });
      return;
    }
    const totalMembers = data.reduce((s, i) => s + Number(i.umubare_w_abaturage || 0), 0);
    const largestCoop = data.reduce((max, i) =>
      Number(i.umubare_w_abaturage || 0) > Number(max?.umubare_w_abaturage || 0) ? i : max, data[0]);
    setStats({
      total: data.length, totalMembers,
      avgMembersPerCoop: (totalMembers / data.length).toFixed(1),
      largestCoop,
      uniqueVillages: new Set(data.map(i => i.umudugudu).filter(Boolean)).size,
    });
  };

  const showAlertMessage = (msg, variant = "success") => {
    setAlert({ show: true, message: msg, variant });
    setTimeout(() => setAlert({ show: false, message: "", variant: "" }), 4500);
  };

  // ────────────────────────────────────────────────
  // Data
  // ────────────────────────────────────────────────
  const fetchIsibo = async () => {
    setLoading(true);
    try {
      const res = await axios.get(API_URL);
      const data = Array.isArray(res.data) ? res.data : (res.data?.data ?? res.data?.isibo ?? []);
      setIsiboList(data);
      setFilteredData(data);
      calculateStats(data);
    } catch { showAlertMessage("Habaye ikosa mu kugira amakuru y'isibo", "danger"); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchIsibo(); }, []);

  // Fetch assigned village name for Isibo users
  useEffect(() => {
    axios.get("/my-info").then(res => {
      const village = res.data.mudugudu_name || "";
      setAssignedVillage(village);
      setFormData(prev => ({ ...prev, umudugudu: village }));
    }).catch(() => {});
  }, []);

  useEffect(() => {
    let result = [...isiboList];
    if (searchTerm) {
      const t = searchTerm.toLowerCase();
      result = result.filter(i =>
        i.izina_ry_isibo?.toLowerCase().includes(t) ||
        i.umuyobozi_w_isibo?.toLowerCase().includes(t) ||
        i.umudugudu?.toLowerCase().includes(t)
      );
    }
    result.sort((a, b) => {
      let va, vb;
      switch (sortBy) {
        case "name": va = a.izina_ry_isibo?.toLowerCase() || ""; vb = b.izina_ry_isibo?.toLowerCase() || ""; break;
        case "members": va = Number(a.umubare_w_abaturage || 0); vb = Number(b.umubare_w_abaturage || 0); break;
        case "village": va = a.umudugudu?.toLowerCase() || ""; vb = b.umudugudu?.toLowerCase() || ""; break;
        default: return 0;
      }
      return sortOrder === "asc" ? (va > vb ? 1 : -1) : (va < vb ? 1 : -1);
    });
    setFilteredData(result);
    setCurrentPage(1);
  }, [searchTerm, sortBy, sortOrder, isiboList]);

  const idxLast = currentPage * itemsPerPage;
  const idxFirst = idxLast - itemsPerPage;
  const current = filteredData.slice(idxFirst, idxLast);
  const totalPg = Math.ceil(filteredData.length / itemsPerPage);

  const paginate = (n) => {
    setCurrentPage(n);
    if (topRef.current && isMobile) topRef.current.scrollIntoView({ behavior: "smooth" });
  };

  // ────────────────────────────────────────────────
  // Form & Actions
  // ────────────────────────────────────────────────
  const validateForm = () => {
    const errors = {};
    if (!formData.izina_ry_isibo?.trim()) errors.izina_ry_isibo = "Izina ry'isibo ni ngombwa";
    if (!formData.umuyobozi_w_isibo?.trim()) errors.umuyobozi_w_isibo = "Amazina y'umuyobozi ni ngombwa";
    if (!formData.umubare_w_abaturage || Number(formData.umubare_w_abaturage) < 1)
      errors.umubare_w_abaturage = "Shyiramo umubare wibura 1";
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    if (formErrors[name]) setFormErrors({ ...formErrors, [name]: "" });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return showAlertMessage("Uzuza neza amakuru yose!", "warning");
    // Always enforce assigned village for Isibo users
    const submitData = isIsiboUser
      ? { ...formData, umudugudu: assignedVillage }
      : formData;
    try {
      if (editingId) {
        await axios.put(`${API_URL}/${editingId}`, submitData);
        showAlertMessage("Isibo yavuguruwe neza!", "success");
      } else {
        await axios.post(API_URL, submitData);
        showAlertMessage("Isibo yashyizwemo neza!", "success");
      }
      resetForm(); setShow(false); fetchIsibo();
      if (navigator.vibrate) navigator.vibrate(100);
    } catch { showAlertMessage("Habaye ikosa mu kubika!", "danger"); }
  };

  const resetForm = () => {
    setFormData({ izina_ry_isibo: "", umuyobozi_w_isibo: "", umubare_w_abaturage: "", umudugudu: assignedVillage });
    setFormErrors({}); setEditingId(null);
  };

  const handleAdd = () => { resetForm(); setShow(true); };
  const handleEdit = (item) => {
    setEditingId(item.isibo_id);
    setFormData({
      izina_ry_isibo: item.izina_ry_isibo,
      umuyobozi_w_isibo: item.umuyobozi_w_isibo,
      umubare_w_abaturage: item.umubare_w_abaturage,
      // Always use assigned village for Isibo users; allow edit for Mudugudu
      umudugudu: isIsiboUser ? assignedVillage : (item.umudugudu || assignedVillage),
    });
    setFormErrors({}); setShow(true);
  };
  const handleViewDetails = (item) => { setSelectedItem(item); setShowDetails(true); };
  const handleDelete = (id) => {
    const item = isiboList.find(i => i.isibo_id === id);
    if (!item) return;
    setItemToDelete(item); setShowDeleteConfirm(true);
  };
  const confirmDelete = async () => {
    if (!itemToDelete) return;
    try {
      await axios.delete(`${API_URL}/${itemToDelete.isibo_id}`);
      showAlertMessage("Isibo yasibwe neza!", "success"); fetchIsibo();
    } catch { showAlertMessage("Habaye ikosa mu gusiba!", "danger"); }
    finally { setShowDeleteConfirm(false); setItemToDelete(null); }
  };

  const exportToCSV = () => {
    const headers = ["Izina ry'Isibo", "Umuyobozi", "Abaturage", "Umudugudu"];
    const csvData = filteredData.map(i => [
      i.izina_ry_isibo, i.umuyobozi_w_isibo, i.umubare_w_abaturage, i.umudugudu || "-"
    ]);
    const csv = [headers, ...csvData].map(r => r.join(",")).join("\n");
    const link = document.createElement("a");
    link.href = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8;" }));
    link.download = `isibo_${new Date().toISOString().split("T")[0]}.csv`;
    link.click();
    showAlertMessage("Dosiye yakoboye neza!", "success");
  };

  // ─── Responsive Values ─────────────────────────────────
  const containerPadding = isExtraSmall ? "0.5rem" : isSmall ? "0.65rem" : isMobile ? "0.85rem" : "1rem";
  const headerFontSize = isExtraSmall ? "0.95rem" : isSmall ? "1.05rem" : isMobile ? "1.2rem" : "1.45rem";
  const statValueSize = isExtraSmall ? "1.3rem" : isSmall ? "1.5rem" : isMobile ? "1.7rem" : "2.25rem";
  const statTitleSize = isExtraSmall ? "0.58rem" : isSmall ? "0.63rem" : "0.78rem";
  const iconRingSize = isExtraSmall ? 38 : isSmall ? 46 : isMobile ? 54 : 70;
  const iconInnerSize = isExtraSmall ? 28 : isSmall ? 34 : isMobile ? 40 : 54;
  const iconFontSize = isExtraSmall ? "0.8rem" : isSmall ? "0.95rem" : isMobile ? "1.1rem" : "1.5rem";

  // ────────────────────────────────────────────────
  // Mobile Card Renderer
  // ────────────────────────────────────────────────
  const renderMobileCard = (item, idx) => (
    <div
      key={item.isibo_id}
      style={{
        background: "var(--theme-bg-card, rgba(15,23,42,0.65))",
        border: `1px solid ${theme.border}`,
        borderRadius: isExtraSmall ? "12px" : "16px",
        padding: isExtraSmall ? "0.6rem" : "0.85rem",
        marginBottom: "0.6rem",
        WebkitTapHighlightColor: "transparent",
      }}
    >
      {/* Header */}
      <div className="d-flex justify-content-between align-items-start" style={{ marginBottom: "0.5rem" }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{
            fontWeight: 700, fontSize: isExtraSmall ? "0.85rem" : "0.95rem",
            color: "var(--theme-text-main, #f1f5f9)", marginBottom: "0.15rem",
            whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
          }}>
            <i className="bi bi-people-fill me-1" style={{ color: theme.primary, fontSize: "0.8rem" }}></i>
            {item.izina_ry_isibo}
          </div>
          <div className="d-flex gap-1 flex-wrap">
            <Badge bg="success" pill style={{ fontSize: isExtraSmall ? "0.55rem" : "0.6rem" }}>
              <i className="bi bi-person-fill me-1"></i>
              {item.umubare_w_abaturage || 0}
            </Badge>
            {item.umudugudu && (
              <Badge bg="warning" pill style={{ fontSize: isExtraSmall ? "0.55rem" : "0.6rem" }}>
                <i className="bi bi-geo-alt me-1"></i>
                {item.umudugudu}
              </Badge>
            )}
          </div>
        </div>
        <span style={{ color: "var(--theme-text-dim, #64748b)", fontSize: "0.65rem", flexShrink: 0, marginLeft: "0.4rem" }}>
          #{idxFirst + idx + 1}
        </span>
      </div>

      {/* Info */}
      <div style={{
        background: "var(--theme-bg, #0a0e1a)", borderRadius: "10px", padding: "0.5rem",
        border: `1px solid ${theme.border}`, marginBottom: "0.5rem",
      }}>
        <div style={{
          fontSize: "0.55rem", color: "var(--theme-text-muted, #94a3b8)",
          textTransform: "uppercase", letterSpacing: "0.3px", marginBottom: "0.15rem",
        }}>Umuyobozi</div>
        <div style={{
          fontSize: isExtraSmall ? "0.75rem" : "0.82rem", color: "var(--theme-text-main, #f1f5f9)",
          fontWeight: 500, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
        }}>
          <i className="bi bi-person-badge me-1" style={{ color: theme.success }}></i>
          {item.umuyobozi_w_isibo || "—"}
        </div>
      </div>

      {/* Actions */}
      <div className="d-flex gap-2" style={{ borderTop: `1px solid ${theme.border}`, paddingTop: "0.5rem" }}>
        <Button variant="outline-info" size="sm" className="flex-fill" style={{
          borderRadius: "10px", fontSize: isExtraSmall ? "0.63rem" : "0.68rem",
          minHeight: "34px", borderColor: "#22d3ee", color: "#22d3ee",
          WebkitTapHighlightColor: "transparent", touchAction: "manipulation",
        }} onClick={() => handleViewDetails(item)}>
          <i className="bi bi-eye me-1"></i>{!isExtraSmall && "Reba"}
        </Button>
        <Button variant="outline-primary" size="sm" className="flex-fill" style={{
          borderRadius: "10px", fontSize: isExtraSmall ? "0.63rem" : "0.68rem",
          minHeight: "34px", borderColor: "#6366f1", color: "#6366f1",
          WebkitTapHighlightColor: "transparent", touchAction: "manipulation",
        }} onClick={() => handleEdit(item)}>
          <i className="bi bi-pencil me-1"></i>{!isExtraSmall && "Hindura"}
        </Button>
        <Button variant="outline-danger" size="sm" className="flex-fill" style={{
          borderRadius: "10px", fontSize: isExtraSmall ? "0.63rem" : "0.68rem",
          minHeight: "34px", borderColor: "#ef4444", color: "#ef4444",
          WebkitTapHighlightColor: "transparent", touchAction: "manipulation",
        }} onClick={() => handleDelete(item.isibo_id)}>
          <i className="bi bi-trash me-1"></i>{!isExtraSmall && "Siba"}
        </Button>
      </div>
    </div>
  );

  // ────────────────────────────────────────────────
  // Responsive Pagination
  // ────────────────────────────────────────────────
  const renderPagination = () => {
    if (totalPg <= 1) return null;
    const maxVis = isExtraSmall ? 3 : isSmall ? 3 : isMobile ? 5 : 7;
    let startP = Math.max(1, currentPage - Math.floor(maxVis / 2));
    let endP = Math.min(totalPg, startP + maxVis - 1);
    if (endP - startP + 1 < maxVis) startP = Math.max(1, endP - maxVis + 1);
    const pages = [];
    for (let i = startP; i <= endP; i++) pages.push(i);

    return (
      <div className="d-flex justify-content-between align-items-center flex-wrap"
        style={{ marginTop: "0.75rem", gap: "0.5rem" }}>
        <small style={{ color: "var(--theme-text-muted, #94a3b8)", fontSize: isSmall ? "0.63rem" : "0.73rem" }}>
          {idxFirst + 1}–{Math.min(idxLast, filteredData.length)} kuri {filteredData.length}
        </small>
        <Pagination className="mb-0" size={isSmall ? "sm" : undefined}>
          <Pagination.Prev onClick={() => paginate(currentPage - 1)} disabled={currentPage === 1} />
          {startP > 1 && (
            <><Pagination.Item onClick={() => paginate(1)}>1</Pagination.Item>
              {startP > 2 && <Pagination.Ellipsis disabled />}</>
          )}
          {pages.map(p => (
            <Pagination.Item key={p} active={p === currentPage} onClick={() => paginate(p)}>{p}</Pagination.Item>
          ))}
          {endP < totalPg && (
            <>{endP < totalPg - 1 && <Pagination.Ellipsis disabled />}
              <Pagination.Item onClick={() => paginate(totalPg)}>{totalPg}</Pagination.Item></>
          )}
          <Pagination.Next onClick={() => paginate(currentPage + 1)} disabled={currentPage === totalPg} />
        </Pagination>
      </div>
    );
  };

  // ────────────────────────────────────────────────
  // JSX
  // ────────────────────────────────────────────────
  return (
    <div ref={topRef} style={{
      background: "var(--theme-bg, #0a0e1a)",
      color: "var(--theme-text-main, #f1f5f9)",
      minHeight: "100vh",
      padding: containerPadding,
      paddingTop: isMobile ? `max(${containerPadding}, env(safe-area-inset-top, 0px))` : containerPadding,
      paddingBottom: isMobile ? `max(${containerPadding}, env(safe-area-inset-bottom, 0px))` : containerPadding,
      WebkitOverflowScrolling: "touch", overflowX: "hidden",
    }}>

      {/* ═══════════ RESPONSIVE STYLES ═══════════ */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Nunito+Sans:wght@300;400;500;600;700;800&display=swap');
        * {
          font-family: 'Nunito Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
          -webkit-text-size-adjust: 100%; text-size-adjust: 100%; box-sizing: border-box;
        }
        html, body {  }

        .stat-card {
          background: var(--theme-bg-card, rgba(15,23,42,0.65)) !important;
          border: 1px solid rgba(99,102,241,0.12) !important;
          border-radius: ${isSmall ? "14px" : "18px"} !important;
          transition: all 0.3s ease !important;
          overflow: hidden;
        }
        @media (hover: hover) and (min-width: 769px) {
          .stat-card:hover {
            transform: translateY(-4px) scale(1.01) !important;
            box-shadow: 0 16px 32px -8px rgba(99,102,241,0.2) !important;
            border-color: rgba(99,102,241,0.25) !important;
          }
        }
        @media (hover: none) {
          .stat-card:active { transform: scale(0.98) !important; border-color: #6366f1 !important; }
          .stat-card:hover { transform: none !important; }
        }

        .icon-ring {
          width: ${iconRingSize}px; height: ${iconRingSize}px;
          border-radius: 50%; display: flex; align-items: center;
          justify-content: center; position: relative; margin: 0 auto;
        }
        .icon-ring::before {
          content: ''; position: absolute; inset: 0; border-radius: 50%;
          padding: ${isSmall ? "2px" : "3px"};
          background: linear-gradient(135deg, var(--ring-color) 0%, transparent 60%);
          -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
          -webkit-mask-composite: xor; mask-composite: exclude;
        }
        .icon-ring-inner {
          width: ${iconInnerSize}px; height: ${iconInnerSize}px;
          border-radius: 50%; background: var(--theme-bg-card, rgba(15,23,42,0.8));
          display: flex; align-items: center; justify-content: center;
        }

        .modern-table-wrapper {
          background: var(--theme-bg-card, rgba(15,23,42,0.65));
          border: 1px solid rgba(99,102,241,0.12);
          border-radius: ${isSmall ? "12px" : "18px"};
          overflow: hidden;
          box-shadow: 0 8px 24px rgba(0,0,0,0.15);
        }
        .modern-table { background-color: transparent !important; color: var(--theme-text-main, #f1f5f9) !important; margin-bottom: 0 !important; }
        .modern-table thead { background: rgba(99,102,241,0.06) !important; }
        .modern-table thead th {
          background: transparent !important; color: var(--theme-text-muted, #94a3b8) !important;
          font-weight: 600 !important; font-size: ${isTablet ? "0.63rem" : "0.68rem"} !important;
          text-transform: uppercase !important; letter-spacing: 0.5px !important;
          padding: ${isTablet ? "0.7rem 0.5rem" : "1rem 0.75rem"} !important;
          border-bottom: 1px solid rgba(99,102,241,0.12) !important; white-space: nowrap;
        }
        .modern-table tbody tr {
          transition: all 0.2s ease !important;
          border-bottom: 1px solid rgba(99,102,241,0.07) !important;
        }
        @media (hover: hover) {
          .modern-table tbody tr:hover {
            background: rgba(99,102,241,0.05) !important;
          }
        }
        .modern-table tbody td {
          background: transparent !important; color: var(--theme-text-main, #f1f5f9) !important;
          padding: ${isTablet ? "0.7rem 0.5rem" : "1rem 0.75rem"} !important;
          vertical-align: middle !important; border: none !important;
          font-size: ${isTablet ? "0.78rem" : "0.85rem"} !important;
        }
        .modern-table tbody tr:last-child { border-bottom: none !important; }

        .table-scroll-wrapper {
          overflow-x: auto; -webkit-overflow-scrolling: touch; overscroll-behavior-x: contain;
        }
        .table-scroll-wrapper::-webkit-scrollbar { height: ${isMobile ? "0" : "6px"}; }
        .table-scroll-wrapper::-webkit-scrollbar-track { background: transparent; }
        .table-scroll-wrapper::-webkit-scrollbar-thumb { background: #3f3f46; border-radius: 10px; }

        .btn-action {
          width: ${isSmall ? "32px" : "36px"} !important; height: ${isSmall ? "32px" : "36px"} !important;
          padding: 0 !important; display: inline-flex !important;
          align-items: center !important; justify-content: center !important;
          border-radius: 10px !important; transition: all 0.3s ease !important;
          border: 1px solid transparent !important;
          -webkit-tap-highlight-color: transparent; touch-action: manipulation;
        }
        @media (hover: hover) { .btn-action:hover { transform: scale(1.1) !important; } }
        .btn-action.btn-outline-info { border-color: #22d3ee !important; color: #22d3ee !important; }
        .btn-action.btn-outline-info:hover { background: rgba(34,211,238,0.15) !important; }
        .btn-action.btn-outline-primary { border-color: #6366f1 !important; color: #6366f1 !important; }
        .btn-action.btn-outline-primary:hover { background: rgba(99,102,241,0.15) !important; }
        .btn-action.btn-outline-danger { border-color: #ef4444 !important; color: #ef4444 !important; }
        .btn-action.btn-outline-danger:hover { background: rgba(239,68,68,0.15) !important; }

        .btn-primary-modern {
          background: linear-gradient(135deg, #6366f1 0%, #4f46e5 100%) !important;
          border: none !important; border-radius: ${isSmall ? "10px" : "12px"} !important;
          padding: ${isSmall ? "0.55rem 0.8rem" : "0.75rem 1.25rem"} !important;
          font-weight: 600 !important; font-size: ${isSmall ? "0.78rem" : "0.86rem"} !important;
          transition: all 0.25s ease !important;
          box-shadow: 0 4px 14px rgba(99,102,241,0.28) !important;
          min-height: 44px !important;
          -webkit-tap-highlight-color: transparent; touch-action: manipulation;
        }
        @media (hover: hover) {
          .btn-primary-modern:hover {
            transform: translateY(-2px) !important;
            box-shadow: 0 8px 22px rgba(99,102,241,0.38) !important;
          }
        }

        .btn-outline-modern {
          border-color: rgba(99,102,241,0.2) !important; color: var(--theme-text-muted, #94a3b8) !important;
          border-radius: ${isSmall ? "10px" : "12px"} !important;
          padding: ${isSmall ? "0.45rem 0.65rem" : "0.65rem 1rem"} !important;
          transition: all 0.25s ease !important;
          min-height: ${isMobile ? "44px" : "auto"};
          -webkit-tap-highlight-color: transparent; touch-action: manipulation;
        }
        .btn-outline-modern:hover { background: rgba(99,102,241,0.08) !important; border-color: rgba(99,102,241,0.3) !important; color: var(--theme-text-main, #f1f5f9) !important; }

        .filter-card {
          background: var(--theme-bg-card, rgba(15,23,42,0.65)) !important;
          border: 1px solid rgba(99,102,241,0.12) !important;
          border-radius: ${isSmall ? "12px" : "16px"} !important;
        }

        .search-input {
          background: var(--theme-bg, #0a0e1a) !important;
          border: 1px solid rgba(99,102,241,0.18) !important;
          color: var(--theme-text-main, #f1f5f9) !important;
          border-radius: 12px !important;
          transition: all 0.25s ease !important; font-size: 16px !important;
          min-height: ${isMobile ? "44px" : "auto"};
          -webkit-appearance: none; appearance: none;
        }
        .search-input:focus { border-color: #6366f1 !important; box-shadow: 0 0 0 3px rgba(99,102,241,0.15) !important; }
        .search-input::placeholder { color: var(--theme-text-dim, #64748b) !important; }

        .filter-select {
          background: var(--theme-bg, #0a0e1a) !important;
          border: 1px solid rgba(99,102,241,0.18) !important;
          color: var(--theme-text-main, #f1f5f9) !important;
          border-radius: 12px !important;
          padding: ${isSmall ? "0.45rem 0.6rem" : "0.6rem 1rem"} !important;
          font-size: 16px !important; cursor: pointer !important;
          transition: all 0.25s ease !important;
          min-height: ${isMobile ? "44px" : "auto"};
          -webkit-appearance: none; appearance: none;
        }
        .filter-select:focus { border-color: #6366f1 !important; box-shadow: 0 0 0 3px rgba(99,102,241,0.15) !important; }
        .filter-select option { background: var(--theme-bg, #0a0e1a); color: var(--theme-text-main, #f1f5f9); }

        .pagination .page-link {
          background: var(--theme-bg-card, rgba(15,23,42,0.65)) !important;
          border-color: rgba(99,102,241,0.15) !important;
          color: var(--theme-text-muted, #94a3b8) !important;
          border-radius: ${isSmall ? "8px" : "10px"} !important;
          margin: 0 ${isSmall ? "1px" : "2px"} !important;
          transition: all 0.2s ease !important;
          min-width: ${isMobile ? "34px" : "38px"}; height: ${isMobile ? "34px" : "38px"};
          display: flex; align-items: center; justify-content: center;
          font-size: ${isSmall ? "0.68rem" : "0.8rem"};
          -webkit-tap-highlight-color: transparent; touch-action: manipulation;
        }
        .pagination .page-link:hover { background: rgba(99,102,241,0.1) !important; border-color: rgba(99,102,241,0.25) !important; color: var(--theme-text-main, #f1f5f9) !important; }
        .pagination .page-item.active .page-link {
          background: linear-gradient(135deg, #6366f1 0%, #4f46e5 100%) !important;
          border-color: #6366f1 !important; color: #fff !important;
        }

        .modal-content {
          background: var(--theme-bg-card, rgba(15,23,42,0.98)) !important;
          border: 1px solid rgba(99,102,241,0.18) !important;
          border-radius: ${isMobile ? "16px" : "22px"} !important;
          color: var(--theme-text-main, #f1f5f9) !important;
          margin: ${isMobile ? "0.5rem" : "auto"};
          max-height: ${isMobile ? "92vh" : "auto"};
        }
        .modal-header {
          border-bottom: 1px solid rgba(99,102,241,0.12) !important;
          padding: ${isSmall ? "0.75rem 1rem" : "1.25rem 1.5rem"} !important;
        }
        .modal-body {
          padding: ${isSmall ? "0.75rem" : isMobile ? "1rem" : "1.5rem"} !important;
          overflow-y: auto; -webkit-overflow-scrolling: touch;
          max-height: ${isMobile ? "65vh" : "auto"};
        }
        .modal-footer {
          border-top: 1px solid rgba(99,102,241,0.12) !important;
          padding: ${isSmall ? "0.5rem 0.75rem" : "1rem 1.5rem"} !important;
          flex-wrap: wrap; gap: 0.5rem;
        }
        .modal-title { font-size: ${isSmall ? "0.88rem" : isMobile ? "1rem" : "1.15rem"} !important; }

        .form-control, .form-select {
          background: var(--theme-bg, #0a0e1a) !important;
          border: 1px solid rgba(99,102,241,0.2) !important;
          color: var(--theme-text-main, #f1f5f9) !important;
          border-radius: 12px !important;
          padding: ${isSmall ? "0.55rem 0.7rem" : "0.75rem 1rem"} !important;
          font-size: 16px !important; min-height: ${isMobile ? "44px" : "auto"};
          -webkit-appearance: none; appearance: none;
        }
        .form-control:focus, .form-select:focus {
          border-color: #6366f1 !important;
          box-shadow: 0 0 0 3px rgba(99,102,241,0.15) !important;
          background: var(--theme-bg, #0a0e1a) !important;
          color: var(--theme-text-main, #f1f5f9) !important;
        }
        .form-label {
          color: var(--theme-text-muted, #94a3b8) !important; font-weight: 500 !important;
          margin-bottom: 0.4rem !important; font-size: ${isSmall ? "0.78rem" : "0.85rem"} !important;
        }
        .form-control::placeholder { color: var(--theme-text-dim, #64748b) !important; }
        .input-group-text { background: rgba(99,102,241,0.08) !important; border-color: rgba(99,102,241,0.18) !important; color: var(--theme-text-muted, #94a3b8) !important; }

        .badge {
          font-weight: 500 !important; padding: ${isSmall ? "0.35rem 0.55rem" : "0.45rem 0.7rem"} !important;
          font-size: ${isSmall ? "0.6rem" : "0.7rem"} !important; border-radius: 8px !important;
        }
        .alert { border-radius: ${isSmall ? "10px" : "14px"} !important; border: none !important;
          font-size: ${isSmall ? "0.78rem" : "0.88rem"} !important; }
        .empty-state {
          background: var(--theme-bg-card, rgba(15,23,42,0.5));
          border: 1px dashed rgba(99,102,241,0.2); border-radius: ${isSmall ? "14px" : "18px"};
        }

        .mobile-sort-overlay {
          position: fixed; inset: 0; background: rgba(0,0,0,0.55);
          z-index: 1055; opacity: ${showMobileSort ? 1 : 0};
          pointer-events: ${showMobileSort ? "auto" : "none"};
          transition: opacity 0.25s ease;
        }
        .mobile-sort-panel {
          position: fixed; bottom: 0; left: 0; right: 0;
          background: var(--theme-bg-card, rgba(15,23,42,0.98));
          border-top: 1px solid rgba(99,102,241,0.15);
          border-radius: 20px 20px 0 0; padding: 1.25rem;
          padding-bottom: max(1.25rem, env(safe-area-inset-bottom, 0px));
          z-index: 1060;
          transform: translateY(${showMobileSort ? "0" : "100%"});
          transition: transform 0.28s cubic-bezier(0.23, 1, 0.32, 1);
          box-shadow: 0 -8px 32px rgba(0,0,0,0.3);
          max-height: 60vh; overflow-y: auto; -webkit-overflow-scrolling: touch;
        }

        @supports (padding: max(0px)) {
          .safe-pad { padding-left: max(0px, env(safe-area-inset-left)); padding-right: max(0px, env(safe-area-inset-right)); }
        }
        @media (hover: none) {
          button:active, .btn:active { opacity: 0.8 !important; transform: scale(0.97) !important; }
          .card:hover { transform: none !important; box-shadow: none !important; }
        }
        @media (max-width: 280px) {
          .card-body { padding: 0.35rem !important; }
          .btn { font-size: 0.63rem !important; padding: 0.2rem 0.3rem !important; }
          .badge { font-size: 0.48rem !important; padding: 0.18rem 0.3rem !important; }
        }
        @media (max-height: 500px) and (orientation: landscape) {
          .icon-ring { display: none !important; }
          .stat-card .card-body { padding: 0.35rem !important; }
        }
        @media (prefers-reduced-motion: reduce) {
          * { animation-duration: 0.01ms !important; transition-duration: 0.01ms !important; }
        }
        @media print {
          body { background: white !important; color: black !important; }
          .btn, .filter-card, .stat-card { display: none !important; }
          .modern-table, .modern-table th, .modern-table td {
            color: black !important; background: white !important; border: 1px solid #ccc !important;
          }
        }
        ::-webkit-scrollbar { width: ${isMobile ? "0" : "6px"}; height: ${isMobile ? "0" : "6px"}; }
        ::-webkit-scrollbar-track { background: transparent; }
        ::-webkit-scrollbar-thumb { background: rgba(99,102,241,0.2); border-radius: 4px; }
        @media (pointer: coarse) { button, .btn, select, input { min-height: 44px; } }
      `}</style>

      {/* ═══════════ HEADER ═══════════ */}
      <div className="d-flex justify-content-between align-items-start flex-wrap"
        style={{ marginBottom: isMobile ? "0.7rem" : "1.2rem", gap: "0.5rem" }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <h2 className="fw-bold mb-1" style={{ color: theme.primary, fontSize: headerFontSize, lineHeight: 1.3 }}>
            <i className="bi bi-people-fill me-2"></i>
            {isExtraSmall ? "Isibo" : "Isibo z'Umudugudu"}
          </h2>
          {!isShortScreen && (
            <small style={{ color: "var(--theme-text-muted, #94a3b8)", fontSize: isSmall ? "0.63rem" : "0.75rem", display: "block" }}>
              {isSmall ? "Gucunga amasibo" : "Andika unabike amasibo y'umudugudu wawe"}
            </small>
          )}
        </div>
        <div className="d-flex gap-2 flex-shrink-0">
          <Button className="btn-primary-modern" onClick={handleAdd}>
            <i className="bi bi-plus-circle me-1"></i>
            {isExtraSmall ? "+" : isSmall ? "Ongeramo" : "Ongeramo Isibo"}
          </Button>
          <Button className="btn-outline-modern" variant="outline-light" onClick={fetchIsibo}>
            <i className="bi bi-arrow-clockwise"></i>
          </Button>
          <Button className="btn-outline-modern" variant="outline-success" onClick={exportToCSV}>
            <i className="bi bi-download"></i>
            {!isSmall && <span className="ms-1">Excel</span>}
          </Button>
        </div>
      </div>

      {/* ═══════════ ALERT ═══════════ */}
      {alert.show && (
        <Alert variant={alert.variant} dismissible onClose={() => setAlert({ show: false })} className="mb-3 shadow"
          style={{
            position: isMobile ? "fixed" : "relative",
            top: isMobile ? "max(0.5rem, env(safe-area-inset-top, 0px))" : "auto",
            left: isMobile ? "0.5rem" : "auto", right: isMobile ? "0.5rem" : "auto",
            zIndex: isMobile ? 1100 : "auto", margin: isMobile ? 0 : undefined,
          }}>
          {alert.message}
        </Alert>
      )}

      {/* ═══════════ STATS ═══════════ */}
      <Row className="mb-3" style={{ rowGap: isSmall ? "0.35rem" : "0.7rem" }}>
        {[
          { title: isSmall ? "Isibo" : "Isibo Zose", val: stats.total, color: theme.primary, icon: "people-fill" },
          { title: isSmall ? "Abaturage" : "Abaturage Bose", val: stats.totalMembers, color: theme.success, icon: "person-check-fill" },
          { title: isSmall ? "Ikigero" : "Ikigereranyo", val: stats.avgMembersPerCoop, color: theme.info, icon: "bar-chart-fill", sub: isSmall ? "/isibo" : "abaturage / isibo" },
          { title: "Imidugudu", val: stats.uniqueVillages, color: theme.warning, icon: "geo-alt-fill" },
        ].map((s, i) => (
          <Col key={i} xs={6} lg={3} className="mb-1 mb-lg-0">
            <Card className="stat-card border-0 h-100">
              <Card.Body className="text-center" style={{ padding: isExtraSmall ? "0.45rem" : isSmall ? "0.6rem" : "0.9rem" }}>
                {!(isShortScreen && isLandscape) && (
                  <div className="icon-ring mb-2" style={{ "--ring-color": s.color }}>
                    <div className="icon-ring-inner">
                      <i className={`bi bi-${s.icon}`} style={{ fontSize: iconFontSize, color: s.color }}></i>
                    </div>
                  </div>
                )}
                <div style={{ fontSize: statValueSize, fontWeight: 800, color: "var(--theme-text-main, #f1f5f9)", lineHeight: 1.2 }}>
                  {s.val}
                </div>
                <div style={{
                  fontSize: statTitleSize, fontWeight: 600, color: "var(--theme-text-muted, #94a3b8)",
                  textTransform: "uppercase", letterSpacing: "0.3px", marginTop: "0.1rem",
                }}>{s.title}</div>
                {s.sub && !isExtraSmall && (
                  <div style={{ fontSize: "0.6rem", color: "var(--theme-text-dim, #64748b)", marginTop: "0.05rem" }}>{s.sub}</div>
                )}
              </Card.Body>
            </Card>
          </Col>
        ))}
      </Row>

      {/* ═══════════ FILTERS ═══════════ */}
      <Card className="filter-card border-0 shadow mb-3">
        <Card.Body style={{ padding: isSmall ? "0.55rem" : isMobile ? "0.7rem" : "0.9rem" }}>
          {isMobile ? (
            <div className="d-flex gap-2">
              <InputGroup style={{ flex: 1 }}>
                <InputGroup.Text style={{ borderRadius: "12px 0 0 12px", padding: "0.35rem" }}>
                  <i className="bi bi-search"></i>
                </InputGroup.Text>
                <Form.Control placeholder="Shakisha..." value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)} className="search-input"
                  style={{ borderRadius: "0 12px 12px 0" }} />
              </InputGroup>
              <Button variant="outline-light" onClick={() => setShowMobileSort(true)} style={{
                borderRadius: "12px", borderColor: theme.border, minWidth: "44px", minHeight: "44px",
                WebkitTapHighlightColor: "transparent", touchAction: "manipulation",
                color: "var(--theme-text-muted, #94a3b8)",
              }}>
                <i className="bi bi-sort-down"></i>
              </Button>
              {!isExtraSmall && (
                <Button variant="outline-light" onClick={() => setUseCardView(!useCardView)} style={{
                  borderRadius: "12px", borderColor: theme.border, minWidth: "44px", minHeight: "44px",
                  WebkitTapHighlightColor: "transparent", touchAction: "manipulation",
                  color: "var(--theme-text-muted, #94a3b8)",
                }}>
                  <i className={`bi bi-${useCardView ? "table" : "grid-3x3-gap"}`}></i>
                </Button>
              )}
            </div>
          ) : (
            <Row className="g-2 g-md-3">
              <Col md={6}>
                <InputGroup>
                  <InputGroup.Text style={{ borderRadius: "12px 0 0 12px" }}>
                    <i className="bi bi-search"></i>
                  </InputGroup.Text>
                  <Form.Control placeholder="Shakisha izina, umuyobozi, umudugudu..."
                    value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
                    className="search-input" style={{ borderRadius: "0 12px 12px 0" }} />
                </InputGroup>
              </Col>
              <Col xs={6} md={3}>
                <Form.Select value={sortBy} onChange={e => setSortBy(e.target.value)} className="filter-select">
                  <option value="name">Izina</option>
                  <option value="members">Abaturage</option>
                  <option value="village">Umudugudu</option>
                </Form.Select>
              </Col>
              <Col xs={6} md={3}>
                <Button className="btn-outline-modern w-100" variant="outline-light"
                  onClick={() => setSortOrder(sortOrder === "asc" ? "desc" : "asc")}>
                  <i className={`bi bi-sort-${sortOrder === "asc" ? "up" : "down"} me-1`}></i>
                  {sortOrder === "asc" ? "Hasi" : "Hejuru"}
                </Button>
              </Col>
            </Row>
          )}
          {searchTerm && (
            <div className="mt-2">
              <small style={{ color: "var(--theme-text-muted, #94a3b8)", fontSize: isSmall ? "0.65rem" : "0.75rem" }}>
                <i className="bi bi-info-circle me-1"></i>
                Byasanze: <strong style={{ color: theme.primary }}>{filteredData.length}</strong> mu {isiboList.length}
              </small>
            </div>
          )}
        </Card.Body>
      </Card>

      {/* ═══════════ MOBILE SORT PANEL ═══════════ */}
      {isMobile && (
        <>
          <div className="mobile-sort-overlay" onClick={() => setShowMobileSort(false)} />
          <div className="mobile-sort-panel">
            <div style={{ width: "36px", height: "4px", background: "rgba(99,102,241,0.3)", borderRadius: "2px", margin: "0 auto 1rem" }} />
            <h6 style={{ color: "var(--theme-text-main, #f1f5f9)", fontWeight: 700, marginBottom: "0.85rem", fontSize: "0.93rem" }}>
              <i className="bi bi-sort-down me-2"></i>Gutondeka
            </h6>
            <div style={{ marginBottom: "0.85rem" }}>
              <label style={{ color: "var(--theme-text-muted, #94a3b8)", fontSize: "0.73rem", fontWeight: 600, marginBottom: "0.35rem", display: "block" }}>
                Gutondeka ku
              </label>
              <div className="d-flex gap-2 flex-wrap">
                {[{ val: "name", label: "Izina" }, { val: "members", label: "Abaturage" }, { val: "village", label: "Umudugudu" }].map(opt => (
                  <Button key={opt.val} variant={sortBy === opt.val ? "primary" : "outline-light"} size="sm"
                    onClick={() => setSortBy(opt.val)} style={{
                      borderRadius: "10px", flex: 1, minHeight: "38px",
                      borderColor: sortBy === opt.val ? theme.primary : theme.border,
                      WebkitTapHighlightColor: "transparent", fontSize: "0.78rem",
                    }}>{opt.label}</Button>
                ))}
              </div>
            </div>
            <div style={{ marginBottom: "1.25rem" }}>
              <label style={{ color: "var(--theme-text-muted, #94a3b8)", fontSize: "0.73rem", fontWeight: 600, marginBottom: "0.35rem", display: "block" }}>
                Uburyo
              </label>
              <div className="d-flex gap-2">
                {[{ val: "asc", label: "↑ Hasi → Hejuru" }, { val: "desc", label: "↓ Hejuru → Hasi" }].map(opt => (
                  <Button key={opt.val} variant={sortOrder === opt.val ? "primary" : "outline-light"} size="sm"
                    onClick={() => setSortOrder(opt.val)} style={{
                      borderRadius: "10px", flex: 1, minHeight: "38px",
                      borderColor: sortOrder === opt.val ? theme.primary : theme.border,
                      WebkitTapHighlightColor: "transparent", fontSize: "0.78rem",
                    }}>{opt.label}</Button>
                ))}
              </div>
            </div>
            <Button variant="primary" className="w-100" onClick={() => setShowMobileSort(false)} style={{
              borderRadius: "12px", minHeight: "44px", background: theme.primary, border: "none",
              WebkitTapHighlightColor: "transparent",
            }}>
              <i className="bi bi-check-lg me-1"></i>Emeza
            </Button>
          </div>
        </>
      )}

      {/* ═══════════ DATA ═══════════ */}
      {loading ? (
        <div className="text-center py-5">
          <Spinner animation="border" style={{ color: theme.primary, width: isSmall ? "2rem" : "3rem", height: isSmall ? "2rem" : "3rem" }} />
          <p className="mt-3" style={{ color: "var(--theme-text-muted, #94a3b8)", fontSize: isSmall ? "0.78rem" : "0.88rem" }}>Gutegura amakuru...</p>
        </div>
      ) : current.length === 0 ? (
        <div className="empty-state text-center" style={{ padding: isSmall ? "1.5rem 0.7rem" : "3rem" }}>
          <i className="bi bi-people-fill" style={{ fontSize: isSmall ? "2.5rem" : "4rem", color: "rgba(99,102,241,0.2)" }}></i>
          <h4 className="mt-3" style={{ color: "var(--theme-text-muted, #94a3b8)", fontSize: isSmall ? "0.95rem" : "1.15rem" }}>Nta isibo ihari</h4>
          <p style={{ color: "var(--theme-text-dim, #64748b)", fontSize: isSmall ? "0.72rem" : "0.82rem" }}>
            {searchTerm ? "Hindura igishakwa" : "Tangira ukongeramo isibo nshya"}
          </p>
          <Button className="btn-primary-modern mt-2" onClick={handleAdd}>
            <i className="bi bi-plus-circle me-2"></i>Ongeramo Isibo
          </Button>
        </div>
      ) : (
        <>
          {isMobile && useCardView ? (
            <div>
              <div style={{ fontSize: "0.63rem", color: "var(--theme-text-muted, #94a3b8)", marginBottom: "0.35rem", textTransform: "uppercase", letterSpacing: "0.5px", fontWeight: 600 }}>
                {filteredData.length} isibo
              </div>
              {current.map((item, idx) => renderMobileCard(item, idx))}
            </div>
          ) : (
            <div className="modern-table-wrapper">
              <div className="table-scroll-wrapper">
                <Table hover className="modern-table">
                  <thead>
                    <tr>
                      <th className="text-center" style={{ minWidth: "40px" }}>#</th>
                      <th style={{ minWidth: "150px" }}>Izina ry'Isibo</th>
                      <th style={{ minWidth: "140px" }}>Umuyobozi</th>
                      <th className="text-center" style={{ minWidth: "80px" }}>Abaturage</th>
                      <th style={{ minWidth: "110px" }}>Umudugudu</th>
                      <th className="text-center" style={{ minWidth: isTablet ? "100px" : "130px" }}>Ibikorwa</th>
                    </tr>
                  </thead>
                  <tbody>
                    {current.map((item, i) => (
                      <tr key={item.isibo_id}>
                        <td className="text-center" style={{ color: "var(--theme-text-dim, #64748b)" }}>{idxFirst + i + 1}</td>
                        <td><div className="fw-semibold" style={{ color: "var(--theme-text-main, #f1f5f9)" }}>{item.izina_ry_isibo}</div></td>
                        <td>{item.umuyobozi_w_isibo || "—"}</td>
                        <td className="text-center"><Badge bg="success" pill>{item.umubare_w_abaturage || 0}</Badge></td>
                        <td>{item.umudugudu || "—"}</td>
                        <td className="text-center">
                          <div className="d-flex gap-1 justify-content-center">
                            <Button className="btn-action btn-outline-info" onClick={() => handleViewDetails(item)}>
                              <i className="bi bi-eye"></i>
                            </Button>
                            <Button className="btn-action btn-outline-primary" onClick={() => handleEdit(item)}>
                              <i className="bi bi-pencil"></i>
                            </Button>
                            <Button className="btn-action btn-outline-danger" onClick={() => handleDelete(item.isibo_id)}>
                              <i className="bi bi-trash"></i>
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            </div>
          )}
          {renderPagination()}
        </>
      )}

      {/* ═══════════ ADD/EDIT MODAL ═══════════ */}
      <Modal show={show} onHide={() => setShow(false)} size={isMobile ? undefined : "lg"} centered fullscreen={isSmall ? true : undefined}>
        <Modal.Header closeButton closeVariant="white" style={{
          background: `linear-gradient(135deg, ${theme.primary} 0%, ${theme.primaryDark} 100%)`, border: "none"
        }}>
          <Modal.Title className="text-white">
            <i className={`bi ${editingId ? "bi-pencil-square" : "bi-plus-circle"} me-2`}></i>
            {editingId ? (isSmall ? "Hindura" : "Hindura Isibo") : (isSmall ? "Isibo Nshya" : "Andika Isibo Nshya")}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ background: "var(--theme-bg, #0a0e1a)" }}>
          <Form onSubmit={handleSubmit}>
            <Row className="g-3">
              <Col xs={12}>
                <Form.Group>
                  <Form.Label className="fw-semibold">Izina ry'Isibo <span className="text-danger">*</span></Form.Label>
                  <InputGroup>
                    <InputGroup.Text><i className="bi bi-people-fill"></i></InputGroup.Text>
                    <Form.Control name="izina_ry_isibo" value={formData.izina_ry_isibo} onChange={handleChange}
                      placeholder="Urugero: Isibo ya Gatare" isInvalid={!!formErrors.izina_ry_isibo} autoComplete="organization" />
                    <Form.Control.Feedback type="invalid">{formErrors.izina_ry_isibo}</Form.Control.Feedback>
                  </InputGroup>
                </Form.Group>
              </Col>
              <Col xs={12}>
                <Form.Group>
                  <Form.Label className="fw-semibold">Umuyobozi w'Isibo <span className="text-danger">*</span></Form.Label>
                  <InputGroup>
                    <InputGroup.Text><i className="bi bi-person-badge"></i></InputGroup.Text>
                    <Form.Control name="umuyobozi_w_isibo" value={formData.umuyobozi_w_isibo} onChange={handleChange}
                      placeholder="Amazina y'umuyobozi..." isInvalid={!!formErrors.umuyobozi_w_isibo} autoComplete="name" />
                    <Form.Control.Feedback type="invalid">{formErrors.umuyobozi_w_isibo}</Form.Control.Feedback>
                  </InputGroup>
                </Form.Group>
              </Col>
              <Col xs={12} md={6}>
                <Form.Group>
                  <Form.Label className="fw-semibold">Umubare w'Abaturage <span className="text-danger">*</span></Form.Label>
                  <InputGroup>
                    <InputGroup.Text><i className="bi bi-person-fill"></i></InputGroup.Text>
                    <Form.Control type="number" name="umubare_w_abaturage" value={formData.umubare_w_abaturage}
                      onChange={handleChange} min="1" placeholder="Umubare..." isInvalid={!!formErrors.umubare_w_abaturage}
                      inputMode="numeric" />
                    <Form.Control.Feedback type="invalid">{formErrors.umubare_w_abaturage}</Form.Control.Feedback>
                  </InputGroup>
                </Form.Group>
              </Col>
              <Col xs={12} md={6}>
                <Form.Group>
                  <Form.Label className="fw-semibold">Umudugudu</Form.Label>
                  <InputGroup>
                    <InputGroup.Text><i className="bi bi-geo-alt-fill"></i></InputGroup.Text>
                    <Form.Control
                      name="umudugudu"
                      value={formData.umudugudu}
                      onChange={handleChange}
                      placeholder="Umudugudu..."
                      readOnly={isIsiboUser}
                      style={isIsiboUser ? { backgroundColor: "rgba(99,102,241,0.08)", cursor: "not-allowed" } : {}}
                    />
                  </InputGroup>
                  {isIsiboUser && assignedVillage && (
                    <small style={{ color: "#94a3b8", fontSize: "0.72rem", marginTop: "0.25rem", display: "block" }}>
                      <i className="bi bi-lock-fill me-1"></i>Umudugudu wawe: {assignedVillage}
                    </small>
                  )}
                </Form.Group>
              </Col>
            </Row>
          </Form>
        </Modal.Body>
        <Modal.Footer style={{ flexDirection: isMobile ? "column" : "row" }}>
          <Button type="submit" onClick={handleSubmit} style={{
            background: editingId
              ? `linear-gradient(135deg, ${theme.warning} 0%, #d97706 100%)`
              : `linear-gradient(135deg, ${theme.success} 0%, #059669 100%)`,
            border: "none", borderRadius: "12px", fontWeight: 600,
            minHeight: isMobile ? "48px" : "auto", width: isMobile ? "100%" : "auto",
            order: isMobile ? 1 : 2, WebkitTapHighlightColor: "transparent",
          }}>
            <i className={`bi me-1 ${editingId ? "bi-save" : "bi-check-circle"}`}></i>
            {editingId ? "Hindura" : "Bika Amakuru"}
          </Button>
          <Button variant="secondary" onClick={() => setShow(false)} style={{
            borderRadius: "12px", minHeight: isMobile ? "44px" : "auto",
            width: isMobile ? "100%" : "auto", order: isMobile ? 2 : 1,
          }}>
            <i className="bi bi-x-circle me-1"></i>Funga
          </Button>
        </Modal.Footer>
      </Modal>

      {/* ═══════════ DETAILS MODAL ═══════════ */}
      <Modal show={showDetails} onHide={() => setShowDetails(false)} centered size={isMobile ? undefined : "lg"}
        fullscreen={isSmall ? true : undefined}>
        <Modal.Header closeButton closeVariant="white" style={{
          background: `linear-gradient(135deg, ${theme.info} 0%, #0891b2 100%)`, border: "none"
        }}>
          <Modal.Title className="text-white">
            <i className="bi bi-eye me-2"></i>{isSmall ? "Amakuru" : "Amakuru Arambuye y'Isibo"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ background: "var(--theme-bg, #0a0e1a)" }}>
          {selectedItem && (
            <div>
              {/* Header Card */}
              <div style={{
                textAlign: "center", marginBottom: "1.25rem", padding: isSmall ? "0.75rem" : "1.25rem",
                background: "var(--theme-bg-card, rgba(15,23,42,0.65))",
                borderRadius: "16px", border: `1px solid rgba(99,102,241,0.12)`,
              }}>
                <div style={{
                  width: isSmall ? "45px" : "60px", height: isSmall ? "45px" : "60px",
                  borderRadius: "50%",
                  background: `linear-gradient(135deg, ${theme.primary} 0%, ${theme.primaryDark} 100%)`,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  margin: "0 auto 0.6rem", fontSize: isSmall ? "1.1rem" : "1.4rem", color: "#fff",
                }}>
                  <i className="bi bi-people-fill"></i>
                </div>
                <h3 className="fw-bold mb-2" style={{ color: theme.primary, fontSize: isSmall ? "1rem" : "1.3rem" }}>
                  {selectedItem.izina_ry_isibo}
                </h3>
                <Badge bg="primary" pill style={{ fontSize: isSmall ? "0.75rem" : "0.9rem", padding: "0.4rem 1rem" }}>
                  <i className="bi bi-people-fill me-1"></i>
                  {selectedItem.umubare_w_abaturage} abaturage
                </Badge>
              </div>

              {/* Details Grid */}
              <div style={{ display: "grid", gridTemplateColumns: isSmall ? "1fr" : "1fr 1fr", gap: "0.75rem" }}>
                {[
                  { label: "Umuyobozi", value: selectedItem.umuyobozi_w_isibo || "—", icon: "person-badge", color: theme.success },
                  { label: "Umudugudu", value: selectedItem.umudugudu || "—", icon: "geo-alt", color: theme.warning },
                  { label: "Abaturage", value: `${selectedItem.umubare_w_abaturage || 0} abantu`, icon: "person-fill", color: theme.info },
                ].map((item, i) => (
                  <div key={i} style={{
                    padding: "0.65rem", background: "var(--theme-bg-card, rgba(15,23,42,0.65))", borderRadius: "12px",
                    border: `1px solid rgba(99,102,241,0.12)`, borderLeft: `3px solid ${item.color}`,
                  }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "0.4rem", marginBottom: "0.25rem" }}>
                      <i className={`bi bi-${item.icon}-fill`} style={{ color: item.color, fontSize: "0.75rem" }}></i>
                      <span style={{
                        fontSize: "0.63rem", color: "var(--theme-text-muted, #94a3b8)",
                        textTransform: "uppercase", letterSpacing: "0.5px", fontWeight: 600,
                      }}>{item.label}</span>
                    </div>
                    <div style={{ fontSize: isSmall ? "0.85rem" : "0.95rem", color: "var(--theme-text-main, #f1f5f9)", fontWeight: 500 }}>
                      {item.value}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer style={{ flexDirection: isMobile ? "column" : "row" }}>
          {selectedItem && (
            <Button onClick={() => { setShowDetails(false); handleEdit(selectedItem); }} style={{
              background: `linear-gradient(135deg, ${theme.primary} 0%, ${theme.primaryDark} 100%)`,
              border: "none", borderRadius: "12px", fontWeight: 600,
              minHeight: isMobile ? "44px" : "auto", width: isMobile ? "100%" : "auto",
              WebkitTapHighlightColor: "transparent",
            }}>
              <i className="bi bi-pencil-square me-1"></i>Hindura
            </Button>
          )}
          <Button variant="secondary" onClick={() => setShowDetails(false)} style={{
            borderRadius: "12px", minHeight: isMobile ? "44px" : "auto",
            width: isMobile ? "100%" : "auto", WebkitTapHighlightColor: "transparent",
          }}>
            <i className="bi bi-x-circle me-1"></i>Funga
          </Button>
        </Modal.Footer>
      </Modal>

      {/* ═══════════ DELETE MODAL ═══════════ */}
      <Modal show={showDeleteConfirm} onHide={() => { setShowDeleteConfirm(false); setItemToDelete(null); }}
        centered backdrop="static" size={isSmall ? "sm" : undefined}>
        <Modal.Header closeButton closeVariant="white" style={{
          background: `linear-gradient(135deg, ${theme.danger} 0%, #dc2626 100%)`, border: "none"
        }}>
          <Modal.Title className="text-white">
            <i className="bi bi-exclamation-triangle-fill me-2"></i>
            {isSmall ? "Gusiba?" : "Uremeza gusiba?"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ background: "var(--theme-bg, #0a0e1a)" }}>
          {itemToDelete && (
            <>
              <p className="mb-1" style={{ color: "var(--theme-text-muted, #94a3b8)", fontSize: isSmall ? "0.82rem" : "0.9rem" }}>
                Ushaka gusiba:
              </p>
              <h5 className="fw-bold mb-3" style={{ color: theme.danger, fontSize: isSmall ? "0.95rem" : "1.1rem" }}>
                {itemToDelete.izina_ry_isibo}
              </h5>
              <div style={{ color: "var(--theme-text-muted, #94a3b8)", fontSize: "0.82rem" }}>
                <div className="mb-1">Umuyobozi: <span style={{ color: "var(--theme-text-main, #f1f5f9)" }}>{itemToDelete.umuyobozi_w_isibo || "—"}</span></div>
              </div>
              <Alert variant="danger" className="mt-3 mb-0" style={{
                background: "rgba(239,68,68,0.12)", border: "1px solid rgba(239,68,68,0.3)",
                color: "#fca5a5", fontSize: isSmall ? "0.78rem" : "0.85rem",
              }}>
                <i className="bi bi-exclamation-triangle me-2"></i>
                Iyi action ntishobora guhindurwa!
              </Alert>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-light" onClick={() => { setShowDeleteConfirm(false); setItemToDelete(null); }} style={{
            borderRadius: "12px", borderColor: "rgba(99,102,241,0.2)",
            minHeight: isMobile ? "44px" : "auto", flex: isMobile ? 1 : "none",
          }}>
            <i className="bi bi-x-circle me-1"></i>Oya
          </Button>
          <Button variant="danger" onClick={confirmDelete} style={{
            borderRadius: "12px", fontWeight: 600,
            minHeight: isMobile ? "44px" : "auto", flex: isMobile ? 1 : "none",
          }}>
            <i className="bi bi-trash me-1"></i>Yego – Siba
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default Isibo;